import { Loader } from "@/components/Common/Loader/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAuthActions } from "@/store/auth";
import { useNotificationStore } from "@/store/notification";
import { REQUIRED_MESSAGE } from "@/utils/form";
import { RETRY_LIMIT, RETRY_TIMEOUT, sendCode, verifyCode } from "@/utils/passwordless";
import {
  verifyEmailVerificationCode,
  resendEmailVerificationCode,
  WorkOSAuthResponse
} from "@/utils/workos";
import env from "@/env";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import * as Yup from "yup";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { useCaptchaToken } from "@/hooks/useCaptchaToken";
import { cn } from "@/lib/utils";

type OTPCodeStepProps = {
  email: string;
  hasBackButton?: boolean;
  onCancel: () => void;
  onComplete?: () => void;
  // Email verification flow props (optional)
  verificationType?: "passwordless" | "email_verification";
  pendingAuthenticationToken?: string;
  emailVerificationId?: string;
  onEmailVerificationComplete?: (response: WorkOSAuthResponse) => void;
};

export const OTPCodeStep = ({
  email,
  hasBackButton,
  onCancel,
  verificationType = "passwordless",
  pendingAuthenticationToken,
  emailVerificationId,
  onEmailVerificationComplete
}: OTPCodeStepProps) => {
  const displayError = useNotificationStore((state) => state.displayError);
  const { completeOtpLogin } = useAuthActions();
  const [sendCount, setSendCount] = useState(1);
  const [canResend, setCanResend] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();
  const [canResendInSeconds, setCanResendInSeconds] = useState(0);
  const {
    captchaResetCounter,
    isCaptchaComplete,
    isCaptchaRequired,
    getCaptchaToken,
    resetCaptcha,
    setCaptchaToken
  } = useCaptchaToken();

  const isEmailVerificationFlow = verificationType === "email_verification";

  const handleResend = () => {
    // Resend is not available for email verification flow (OTP already sent by WorkOS/provider)
    if (isEmailVerificationFlow) {
      return;
    }

    if (sendCount > RETRY_LIMIT) {
      displayError({
        title: "ERROR",
        message: "You have requested OTP too many times. Please try again later",
        isPublic: true
      });
      return;
    }

    (async () => {
      try {
        setIsResending(true);

        if (isEmailVerificationFlow) {
          // Email verification flow - use WorkOS resend API
          if (!pendingAuthenticationToken) {
            throw new Error("Missing authentication token");
          }

          await resendEmailVerificationCode(email, pendingAuthenticationToken, emailVerificationId);

          trackEvent(AnalyticsEventType.OTP_RESENT_CODE_COMPLETED);

          toast({
            title: "Verification OTP sent. Please check your inbox.",
            variant: "success"
          });

          setSendCount((prev) => {
            return prev + 1;
          });
          restrictResend();
        } else {
          // Passwordless flow
          const captcha = await getCaptchaToken();
          await sendCode({ email, captcha });

          trackEvent(AnalyticsEventType.OTP_RESENT_CODE_COMPLETED);

          toast({ title: "OTP sent. Please check your inbox.", variant: "success" });

          setSendCount((prev) => {
            return prev + 1;
          });
          restrictResend();
        }
      } catch (e) {
        trackEvent(AnalyticsEventType.OTP_RESENT_CODE_FAILED);

        displayError({
          title: "ERROR",
          message: (e as Error).message || "Failed to send code",
          isPublic: true
        });
      } finally {
        if (!isEmailVerificationFlow) {
          // Captcha tokens are single-use and can expire quickly.
          resetCaptcha();
        }
        setIsResending(false);
      }
    })();
  };

  const restrictResend = () => {
    setCanResend(false);
    setCanResendInSeconds(RETRY_TIMEOUT / 1000);

    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
    }
    timeoutRef.current = setInterval(() => {
      setCanResendInSeconds((prev) => {
        const value = Math.max(0, prev - 1);

        if (value <= 0) {
          if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
          }
          setCanResend(true);
        }

        return value;
      });
    }, 1000);
  };

  useEffect(() => {
    restrictResend();

    return () => {
      clearInterval(timeoutRef.current as unknown as number);
    };
  }, []);

  return (
    <Formik<OTPCodeFormType>
      initialValues={{
        code: ""
      }}
      validationSchema={OTPFormTypeFormSchema}
      onSubmit={async ({ code }) => {
        try {
          if (isEmailVerificationFlow) {
            // Email verification flow (OAuth)
            if (!pendingAuthenticationToken) {
              throw new Error("Missing authentication token");
            }

            const response = await verifyEmailVerificationCode(
              code,
              pendingAuthenticationToken,
              emailVerificationId
            );

            trackEvent(AnalyticsEventType.OTP_VERIFIED_CODE_COMPLETED);

            if (onEmailVerificationComplete) {
              await onEmailVerificationComplete(response);
            } else {
              // Fallback: use completeOtpLogin if callback not provided
              await completeOtpLogin(response.accessToken, response.is_new_user);
            }
          } else {
            // Passwordless flow
            const response = await verifyCode({ email, code });

            trackEvent(AnalyticsEventType.OTP_VERIFIED_CODE_COMPLETED);

            restrictResend();

            await completeOtpLogin(response.accessToken, response.is_new_user);
          }
        } catch (e) {
          trackEvent(AnalyticsEventType.OTP_VERIFIED_CODE_FAILED);
          let message = (e as Error).message || "Failed to verify code";
          let blocked = false;

          if (message.includes("blocked")) {
            message = "Unable to login. Please contact support for further assistance.";
            blocked = true;
          }

          displayError({
            title: "Authentication failed",
            message,
            isPublic: true
          });

          if (blocked) {
            onCancel();
          }
        }
      }}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleBlur,
        handleChange,
        values,
        isValid,
        isSubmitting,
        touched,
        errors
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>
                Enter OTP sent to your email
                <br />
              </Label>
              <Input
                name="code"
                value={values.code}
                invalid={touched.code && !!errors.code}
                onBlur={handleBlur}
                onChange={handleChange}
                maxLength={6}
              />
              {touched.code && errors.code && (
                <div className="subtext-small capitalize text-red-light-500">{errors.code}</div>
              )}
              <span className="text-sm text-gray-dark-1000">
                OTP sent to <span className="text-foreground">{email}</span>
              </span>
              {!isEmailVerificationFlow && (
                <a
                  className="cursor-pointer text-sm text-foreground hover:underline"
                  onClick={onCancel}
                >
                  Send to a different email?
                </a>
              )}
              {isEmailVerificationFlow && (
                <a
                  className="cursor-pointer text-sm text-foreground hover:underline"
                  onClick={onCancel}
                >
                  Use a different login method?
                </a>
              )}
            </div>

            <div className="flex w-full flex-row items-center gap-3">
              {hasBackButton && (
                <Button
                  type="button"
                  variant={"outline"}
                  className="w-1/2 gap-3 bg-gray-dark-600"
                  onClick={onCancel}
                >
                  <span>Go back</span>
                </Button>
              )}
              <Button
                type="submit"
                variant={"outline"}
                className={cn("w-full flex-1 gap-3 bg-gray-dark-600", hasBackButton && "w-1/2")}
                disabled={!isValid || isSubmitting}
              >
                <span>Verify Code</span>
                {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
              </Button>
            </div>

            {!isEmailVerificationFlow && sendCount > 0 && (
              <>
                <Button
                  type="button"
                  variant={"outline"}
                  className="gap-3 bg-gray-dark-600"
                  disabled={isSubmitting || !canResend || (isCaptchaRequired && !isCaptchaComplete)}
                  onClick={handleResend}
                >
                  <span>Resend {isEmailVerificationFlow ? "Verification Code" : "OTP"}</span>
                  {isResending && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
                </Button>
                {isCaptchaRequired && canResend && (
                  <div className="label-x-small mt-[-10px] text-gray-dark-1000">
                    Please complete the CAPTCHA to resend the code.
                  </div>
                )}

                {!isEmailVerificationFlow && isCaptchaRequired && canResend && (
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      key={captchaResetCounter}
                      sitekey={env.RECAPTCHA_V2_SITE_KEY}
                      onChange={(token: string | null) => setCaptchaToken(token)}
                      onExpired={() => setCaptchaToken(null)}
                      onErrored={() => setCaptchaToken(null)}
                    />
                  </div>
                )}
                <div className="flex flex-col justify-center">
                  <span className="text-sm text-gray-dark-1000">
                    Didn't receive code?
                    <br />
                    {canResend ? (
                      <span>Click resend to request a new code</span>
                    ) : (
                      <>
                        Request {isEmailVerificationFlow ? "verification code" : "OTP"} in{" "}
                        <span className="text-yellow">{canResendInSeconds}</span> seconds
                      </>
                    )}
                  </span>
                </div>
              </>
            )}

            {/* <p className="subtext-small text-gray-dark-1000">
                        <a
                            onClick={onCancel}
                            className="cursor-pointer text-foreground hover:underline"
                        >
                            Cancel
                        </a>
                    </p> */}
          </form>
        );
      }}
    </Formik>
  );
};

export type OTPCodeFormType = {
  code: string;
};

const OTPFormTypeFormSchema = Yup.object().shape({
  code: Yup.string()
    .min(6, "To have a minimum length of 6 characters")
    .max(6, "To have a maximum length of 6 characters")
    .required(REQUIRED_MESSAGE)
});
